import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { HandCoins, Plus, Trash2, User, Landmark, Calendar } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parseFloat(v) || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "";

const emptyForm = {
  partner_name: "",
  amount: "",
  bank_account_id: "",
  contribution_date: new Date().toISOString().split("T")[0],
  notes: "",
};

export default function OwnerContributionsTab({ companyId, bankAccounts = [], onDataChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState({ open: false, data: { ...emptyForm } });

  const fetchAll = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/companies/${companyId}/owner-contributions`);
      setItems(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar RP: " + (e.response?.data?.detail || ""));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, [companyId]);

  const openNew = () => {
    const defaultBank = bankAccounts.find(b => b.is_default);
    setDlg({
      open: true,
      data: { ...emptyForm, bank_account_id: defaultBank?.id || (bankAccounts[0]?.id || "") },
    });
  };

  const save = async () => {
    const d = dlg.data;
    if (!d.partner_name?.trim()) return toast.error("Nome do sócio obrigatório");
    if (!d.amount || parseFloat(d.amount) <= 0) return toast.error("Valor deve ser positivo");
    if (!d.bank_account_id) return toast.error("Selecione a conta bancária que recebe o valor");
    try {
      await axios.post(`${API}/companies/${companyId}/owner-contributions`, {
        partner_name: d.partner_name.trim(),
        amount: parseFloat(d.amount),
        bank_account_id: d.bank_account_id,
        contribution_date: d.contribution_date || null,
        notes: d.notes?.trim() || null,
      });
      toast.success("Aporte registrado e crédito lançado na conta");
      setDlg({ open: false, data: { ...emptyForm } });
      fetchAll();
      if (onDataChange) onDataChange();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Excluir aporte de ${item.partner_name} (${fmt(item.amount)})? O crédito será revertido na conta ${item.bank_account_name || ""}.`)) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/owner-contributions/${item.id}`);
      toast.success("Aporte excluído e saldo revertido");
      fetchAll();
      if (onDataChange) onDataChange();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.detail || ""));
    }
  };

  const totalGeral = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const totalsByPartner = items.reduce((acc, it) => {
    const k = it.partner_name || "—";
    acc[k] = (acc[k] || 0) + (parseFloat(it.amount) || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-4 rp-tab" data-testid="rp-tab">
      <style>{`
        .rp-tab, .rp-tab * { color: #000000 !important; }
        .rp-tab label { font-weight: 700 !important; }
        .rp-tab .rp-total { color: #7c3aed !important; }
        .rp-tab input::placeholder, .rp-tab textarea::placeholder {
          color: #4b5563 !important; opacity: 1 !important;
        }
      `}</style>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl md:text-2xl font-extrabold flex items-center gap-2">
          <HandCoins className="h-6 w-6 text-purple-600" /> RP — Recurso Próprio (Aportes de Sócios)
        </h2>
        <Button className="bg-purple-500 hover:bg-purple-600" onClick={openNew} data-testid="rp-new-btn">
          <Plus className="h-4 w-4 mr-1" /> Novo Aporte
        </Button>
      </div>

      {/* Card total geral */}
      <Card className="bg-gradient-to-r from-purple-50 to-fuchsia-50 border-purple-200">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs font-bold">Total Aportado</p>
            <p className="text-2xl md:text-3xl font-extrabold rp-total" data-testid="rp-total">{fmt(totalGeral)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold">{items.length} aporte(s)</p>
          </div>
        </CardContent>
      </Card>

      {/* Totais por sócio */}
      {Object.keys(totalsByPartner).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(totalsByPartner).map(([partner, total]) => (
            <Card key={partner}>
              <CardContent className="p-3">
                <p className="text-xs font-bold flex items-center gap-1"><User className="h-3 w-3" /> {partner}</p>
                <p className="text-lg font-bold rp-total mt-1">{fmt(total)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <p className="text-center py-4 font-bold">Carregando...</p>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-6 text-center font-bold">Nenhum aporte registrado</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-purple-50">
                <tr className="border-b">
                  <th className="text-left p-3 font-bold">Data</th>
                  <th className="text-left p-3 font-bold">Sócio</th>
                  <th className="text-right p-3 font-bold">Valor</th>
                  <th className="text-left p-3 font-bold">Conta que Recebeu</th>
                  <th className="text-left p-3 font-bold">Observações</th>
                  <th className="text-center p-3 font-bold">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-b hover:bg-purple-50" data-testid={`rp-row-${it.id}`}>
                    <td className="p-3"><span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(it.contribution_date)}</span></td>
                    <td className="p-3 font-bold">{it.partner_name}</td>
                    <td className="p-3 text-right font-extrabold rp-total">{fmt(it.amount)}</td>
                    <td className="p-3"><span className="inline-flex items-center gap-1"><Landmark className="h-3 w-3" /> {it.bank_account_name || "—"}</span></td>
                    <td className="p-3 text-xs">{it.notes || "—"}</td>
                    <td className="p-3 text-center">
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(it)} data-testid={`rp-delete-${it.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Dialog novo aporte */}
      <Dialog open={dlg.open} onOpenChange={(open) => setDlg({ ...dlg, open })}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto rp-tab">
          <DialogHeader>
            <DialogTitle className="font-extrabold flex items-center gap-2"><HandCoins className="h-5 w-5 text-purple-600" /> Novo Aporte de Sócio</DialogTitle>
            <DialogDescription>Registra a entrada de recurso próprio do sócio. Cria uma transação de entrada e credita a conta selecionada.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold">Nome do Sócio *</label>
              <Input value={dlg.data.partner_name} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, partner_name: e.target.value } })} placeholder="Ex: João Silva" data-testid="rp-partner-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold">Valor Aportado (R$) *</label>
                <Input type="number" step="0.01" value={dlg.data.amount} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, amount: e.target.value } })} placeholder="0.00" data-testid="rp-amount" />
              </div>
              <div>
                <label className="text-xs font-bold">Data do Aporte</label>
                <Input type="date" value={dlg.data.contribution_date} onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, contribution_date: e.target.value } })} data-testid="rp-date" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold">Conta Bancária que recebe o recurso *</label>
              <Select value={dlg.data.bank_account_id} onValueChange={(v) => setDlg({ ...dlg, data: { ...dlg.data, bank_account_id: v } })}>
                <SelectTrigger data-testid="rp-bank-account"><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}{b.bank_name ? ` — ${b.bank_name}` : ""} · Saldo: {fmt(b.current_balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bankAccounts.length === 0 && (
                <p className="text-xs text-red-600 mt-1 font-bold">Nenhuma conta cadastrada. Cadastre em &quot;Saldo de Contas&quot; antes de registrar aportes.</p>
              )}
            </div>
            <div>
              <label className="text-xs font-bold">Observações</label>
              <textarea
                className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={dlg.data.notes}
                onChange={(e) => setDlg({ ...dlg, data: { ...dlg.data, notes: e.target.value } })}
                placeholder="Ex: Aporte para capital de giro, mês 07/2026"
                data-testid="rp-notes"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDlg({ open: false, data: { ...emptyForm } })}>Cancelar</Button>
              <Button className="bg-purple-500 hover:bg-purple-600" onClick={save} data-testid="rp-save-btn">Registrar Aporte</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
